import type {SummaryGuide, TopicWeight} from 'iq-onboarding/types';

// --- Resume upload ---

// Mirrors the backend's maxResumeUploadBytes (the S3 POST policy also enforces this).
export const MAX_RESUME_BYTES = 20 * 1024 * 1024; // 20 MB

export type ResumeFileType = 'pdf';

export interface ResumeFileInfo {
  ext: ResumeFileType;
  mimeType: string;
}

const MIME_TYPES: Record<ResumeFileType, string> = {
  pdf: 'application/pdf',
};

export async function validateResumeFile(file: File): Promise<ResumeFileInfo | null> {
  const name = file.name.toLowerCase();

  if (name.endsWith('.pdf')) {
    // Check %PDF magic bytes.
    const buf = await file.slice(0, 4).arrayBuffer();
    const b = new Uint8Array(buf);
    if (b[0] === 0x25 && b[1] === 0x50 && b[2] === 0x44 && b[3] === 0x46) {
      return {ext: 'pdf', mimeType: MIME_TYPES.pdf};
    }
    return null;
  }

  return null;
}

// --- Guides ---

export const guideKey = (g: SummaryGuide) => `${g.source}-${g.slug}`;

// pickTopGuides favours distinct companies: it first takes one guide per company
// (in the incoming relevancy order), then tops up with the remaining guides if
// there aren't enough distinct companies to fill the cap.
export function pickTopGuides(guides: SummaryGuide[], max: number): SummaryGuide[] {
  const picked: SummaryGuide[] = [];
  const pickedKeys = new Set<string>();
  const seenCompanies = new Set<number>();

  for (const g of guides) {
    if (picked.length >= max) break;
    if (seenCompanies.has(g.company_id)) continue;
    seenCompanies.add(g.company_id);
    picked.push(g);
    pickedKeys.add(guideKey(g));
  }
  for (const g of guides) {
    if (picked.length >= max) break;
    if (pickedKeys.has(guideKey(g))) continue;
    picked.push(g);
    pickedKeys.add(guideKey(g));
  }
  return picked;
}

// --- Skillset radar ---

export type RadarAxis = {topic: string; current: number; target: number};

// Cap on how many axes we draw before dropping current-only topics (see selectAxes).
const MAX_AXES = 8;

// selectAxes merges both skillsets into one set of radar axes. Every target topic is
// always kept; current-only topics (not in the target) are included too, but dropped
// when the union would exceed MAX_AXES so the chart stays legible. Axes are ordered by
// descending weight (target first, current as tiebreaker).
export function selectAxes(current: TopicWeight[], target: TopicWeight[]): RadarAxis[] {
  const byId = new Map<number, RadarAxis>();
  for (const t of current) byId.set(t.id, {topic: t.topic, current: t.weight, target: 0});
  for (const t of target) {
    const existing = byId.get(t.id);
    if (existing) existing.target = t.weight;
    else byId.set(t.id, {topic: t.topic, current: 0, target: t.weight});
  }

  const all = [...byId.values()];
  const targetAxes = all.filter((a) => a.target > 0);
  // Keep current-only topics only when there's room; never drop target topics. When
  // there are no target topics yet (resume uploaded, nothing selected), fall back to
  // the full set so the radar still renders.
  const shown = all.length > MAX_AXES && targetAxes.length > 0 ? targetAxes : all;

  return shown.sort((a, b) => b.target - a.target || b.current - a.current);
}

// wrapLabel breaks long topic names onto a second line at a space so axis labels like
// "Concurrent & Distributed Systems" don't get truncated by the chart edge.
export function wrapLabel(name: string): string {
  if (name.length <= 12) return name;
  const mid = Math.floor(name.length / 2);
  const left = name.lastIndexOf(' ', mid);
  const right = name.indexOf(' ', mid);
  const split = left === -1 ? right : right === -1 || mid - left <= right - mid ? left : right;
  return split === -1 ? name : name.slice(0, split) + '\n' + name.slice(split + 1);
}
